import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Bell, BellOff, ChevronDown, CircleCheck, ListFilter, MessageSquareReply, Pencil, Search, SendHorizontal, SmilePlus, Trash2, X, AtSign } from '@/components/ui/icons';
import type { Activity, Comment, Task } from '@shared/types';
import { priorityMeta } from '@shared/constants';
import { useDb, useMe, type Tables } from '@/lib/store';
import { effectiveStatuses } from '@/lib/hierarchy';
import { addComment, deleteComment, toggleReaction, toggleWatcher, updateComment } from '@/lib/actions';
import { formatDue, formatDuration, formatWhen, formatWhenLong } from '@/lib/format';
import { Avatar } from '@/components/ui/Avatar';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { RichEditor, RichText, type RichEditorHandle } from '@/components/editor/RichEditor';
import { PeopleMenu } from '@/components/fields/People';

const REACTIONS = ['👍', '❤️', '🎉', '😄', '👀', '🙏', '✅', '🔥'];

function StatusChip({ db, listId, statusId }: { db: Tables; listId: string; statusId: unknown }) {
  const s = effectiveStatuses(db, listId).find((x) => x.id === statusId);
  if (!s) return <strong>unknown</strong>;
  return (
    <span className="act-status">
      <span className="act-status-sq" style={{ background: s.color }} />
      {s.name}
    </span>
  );
}

/** The sentence after the actor's name for an activity entry (also used by the chat profile panel). */
export function describeActivity(db: Tables, a: Activity, task: Task): React.ReactNode {
  const user = (id: unknown) => <strong>{db.users[String(id)]?.name ?? 'someone'}</strong>;
  switch (a.kind) {
    case 'created':
      return a.meta?.duplicatedFrom ? 'duplicated this task' : 'created this task';
    case 'status':
      return (
        <>
          changed status from <StatusChip db={db} listId={task.listId} statusId={a.from} /> to <StatusChip db={db} listId={task.listId} statusId={a.to} />
        </>
      );
    case 'name':
      return (
        <>
          renamed this task to <strong>{String(a.to)}</strong>
        </>
      );
    case 'assignee_add':
      return a.to === a.actorId ? 'assigned to themselves' : <>assigned to {user(a.to)}</>;
    case 'assignee_remove':
      return <>removed assignee {user(a.from)}</>;
    case 'priority':
      return a.to ? (
        <>
          set priority to <strong>{priorityMeta(a.to as 1)?.label}</strong>
        </>
      ) : (
        'removed priority'
      );
    case 'due_date':
      return a.to ? (
        <>
          changed due date to <strong>{formatDue(a.to as number)}</strong>
        </>
      ) : (
        'removed due date'
      );
    case 'start_date':
      return a.to ? (
        <>
          changed start date to <strong>{formatDue(a.to as number)}</strong>
        </>
      ) : (
        'removed start date'
      );
    case 'tag_add':
      return (
        <>
          added tag <strong>{db.tags[String(a.to)]?.name ?? 'tag'}</strong>
        </>
      );
    case 'tag_remove':
      return (
        <>
          removed tag <strong>{db.tags[String(a.from)]?.name ?? 'tag'}</strong>
        </>
      );
    case 'moved':
      return (
        <>
          moved this task from <strong>{db.lists[String(a.from)]?.name ?? 'a list'}</strong> to <strong>{db.lists[String(a.to)]?.name ?? 'a list'}</strong>
        </>
      );
    case 'attachment':
      return (
        <>
          attached <strong>{String(a.to)}</strong>
        </>
      );
    case 'subtask_add':
      return (
        <>
          added subtask <strong>{db.tasks[String(a.to)]?.name ?? ''}</strong>
        </>
      );
    case 'custom_field': {
      const f = db.customFields[String(a.meta?.fieldId)];
      return (
        <>
          updated <strong>{f?.name ?? 'a field'}</strong>
        </>
      );
    }
    case 'time_estimate':
      return a.to ? (
        <>
          set time estimate to <strong>{formatDuration(a.to as number)}</strong>
        </>
      ) : (
        'removed time estimate'
      );
    case 'task_type':
      return (
        <>
          changed task type to <strong>{(a.to ? db.taskTypes[String(a.to)]?.name : undefined) ?? 'Task'}</strong>
        </>
      );
    case 'archived':
      return a.to ? 'archived this task' : 'unarchived this task';
    case 'dependency':
      return (
        <>
          added a {a.meta?.type === 'blocking' ? 'dependency' : 'link'} to <strong>{db.tasks[String(a.to)]?.name ?? 'a task'}</strong>
        </>
      );
    default:
      return 'updated this task';
  }
}

function Reactions({ comment }: { comment: Comment }) {
  const me = useMe();
  const users = useDb((db) => db.users);
  const entries = Object.entries(comment.reactions ?? {});
  if (!entries.length) return null;
  return (
    <div className="reactions">
      {entries.map(([emoji, ids]) => (
        <Tooltip key={emoji} label={ids.map((u) => users[u]?.name).join(', ')}>
          <button className={clsx('reaction', { mine: ids.includes(me) })} onClick={() => toggleReaction('comments', comment.id, emoji)}>
            {emoji} <span>{ids.length}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

function CommentCard({ comment, replies, taskId }: { comment: Comment; replies: Comment[]; taskId: string }) {
  const me = useMe();
  const author = useDb((db) => db.users[comment.authorId]);
  const assignee = useDb((db) => (comment.assigneeId ? db.users[comment.assigneeId] : undefined));
  const [editing, setEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replying, setReplying] = useState(false);
  const editRef = useRef<RichEditorHandle>(null);
  const replyRef = useRef<RichEditorHandle>(null);
  const mine = comment.authorId === me;
  return (
    <div className={clsx('comment', { assigned: !!comment.assigneeId && !comment.resolved })}>
      <div className="comment-head">
        <Avatar userId={comment.authorId} size={24} />
        <span className="comment-author">{author?.name ?? 'Unknown'}</span>
        <Tooltip label={formatWhenLong(comment.createdAt)}>
          <span className="comment-time">{formatWhen(comment.createdAt)}</span>
        </Tooltip>
        {comment.editedAt && <span className="faint" style={{ fontSize: 'var(--fs-xs)' }}>(edited)</span>}
        <span className="spacer" />
        <div className="comment-actions">
          <Popover
            content={(close) => (
              <div className="emoji-row">
                {REACTIONS.map((e) => (
                  <button key={e} onClick={() => (toggleReaction('comments', comment.id, e), close())}>
                    {e}
                  </button>
                ))}
              </div>
            )}
          >
            <button className="icon-btn xs" title="React">
              <SmilePlus size={14} />
            </button>
          </Popover>
          <button className="icon-btn xs" title="Reply" onClick={() => (setReplying(true), setShowReplies(true))}>
            <MessageSquareReply size={14} />
          </button>
          {mine && (
            <>
              <button className="icon-btn xs" title="Edit" onClick={() => setEditing(true)}>
                <Pencil size={13} />
              </button>
              <button className="icon-btn xs" title="Delete" onClick={() => confirm('Delete this comment?') && deleteComment(comment.id)}>
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>
      {assignee && (
        <div className={clsx('comment-assigned', { resolved: comment.resolved })}>
          <AtSign size={12} /> Assigned to {assignee.name}
          {comment.assigneeId === me || mine ? (
            <button className="btn sm" onClick={() => updateComment(comment.id, { resolved: !comment.resolved })}>
              <CircleCheck size={13} /> {comment.resolved ? 'Reopen' : 'Resolve'}
            </button>
          ) : null}
        </div>
      )}
      {editing ? (
        <div className="comment-edit">
          <RichEditor ref={editRef} content={comment.body} autofocus onSubmit={(html) => (updateComment(comment.id, { body: html, editedAt: Date.now() }), setEditing(false))} />
          <div className="row gap-6" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button className="btn sm primary" onClick={() => (updateComment(comment.id, { body: editRef.current?.getHTML() ?? comment.body, editedAt: Date.now() }), setEditing(false))}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <RichText html={comment.body} className="comment-body" />
      )}
      <Reactions comment={comment} />
      {replies.length > 0 && (
        <button className="comment-replies-toggle" onClick={() => setShowReplies(!showReplies)}>
          <MessageSquareReply size={13} /> {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}
          <ChevronDown size={12} style={{ transform: showReplies ? 'rotate(180deg)' : undefined }} />
        </button>
      )}
      {showReplies && (
        <div className="comment-thread">
          {replies.map((r) => (
            <CommentCard key={r.id} comment={r} replies={[]} taskId={taskId} />
          ))}
          {replying && (
            <div className="composer small">
              <RichEditor
                ref={replyRef}
                content=""
                placeholder="Reply..."
                autofocus
                submitOnEnter
                onSubmit={(html) => {
                  if (replyRef.current?.isEmpty()) return;
                  addComment(taskId, html, { parentId: comment.id });
                  replyRef.current?.clear();
                }}
              />
              <button className="icon-btn sm" onClick={() => setReplying(false)}>
                <X size={14} />
              </button>
            </div>
          )}
          {!replying && (
            <button className="comment-replies-toggle" onClick={() => setReplying(true)}>
              Reply
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ task }: { task: Task }) {
  const ref = useRef<RichEditorHandle>(null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [empty, setEmpty] = useState(true);
  const submit = () => {
    const html = ref.current?.getHTML() ?? '';
    if (!html || ref.current?.isEmpty()) return;
    addComment(task.id, html, { assigneeId: assignee });
    ref.current?.clear();
    setAssignee(null);
    setEmpty(true);
  };
  return (
    <div className="composer">
      <RichEditor ref={ref} content="" placeholder="Write a comment..." submitOnEnter onSubmit={submit} onChange={(h) => setEmpty(h === '<p></p>' || !h)} />
      <div className="composer-bar">
        <Popover
          placement="top-start"
          content={(close) => (
            <PeopleMenu
              single
              title="Assign comment to"
              selected={assignee ? [assignee] : []}
              onChange={(ids) => {
                setAssignee(ids[0] ?? null);
                close();
              }}
            />
          )}
        >
          <button className="composer-mode">
            {assignee ? <Avatar userId={assignee} size={16} /> : null}
            {assignee ? 'Assign' : 'Comment'} <ChevronDown size={12} />
          </button>
        </Popover>
        <button className="icon-btn xs" title="Mention" onClick={() => ref.current?.editor?.chain().focus().insertContent('@').run()}>
          <AtSign size={14} />
        </button>
        <span className="spacer" />
        <button className={clsx('composer-send', { ready: !empty })} onClick={submit} title="Send (Enter)">
          <SendHorizontal size={15} />
        </button>
      </div>
    </div>
  );
}

export function ActivityPanel({ task }: { task: Task }) {
  const me = useMe();
  const db = useDb((d) => d);
  const [filter, setFilter] = useState<'all' | 'comments' | 'activity'>('all');
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const comments = useMemo(() => Object.values(db.comments).filter((c) => c.taskId === task.id), [db.comments, task.id]);
  const activities = useMemo(() => Object.values(db.activities).filter((a) => a.taskId === task.id), [db.activities, task.id]);
  const roots = comments.filter((c) => !c.parentId);
  const repliesOf = (id: string) => comments.filter((c) => c.parentId === id).sort((a, b) => a.createdAt - b.createdAt);
  type Item = { kind: 'activity'; at: number; a: Activity } | { kind: 'comment'; at: number; c: Comment };
  let items: Item[] = [
    ...(filter !== 'comments' ? activities.map((a) => ({ kind: 'activity' as const, at: a.createdAt, a })) : []),
    ...(filter !== 'activity' ? roots.map((c) => ({ kind: 'comment' as const, at: c.createdAt, c })) : []),
  ].sort((a, b) => a.at - b.at);
  if (q) {
    const ql = q.toLowerCase();
    items = items.filter((i) => (i.kind === 'comment' ? i.c.body.toLowerCase().includes(ql) : false));
  }
  const hiddenCount = !showAll && items.filter((i) => i.kind === 'activity').length > 6 ? items.filter((i) => i.kind === 'activity').length - 4 : 0;
  let visible = items;
  if (hiddenCount) {
    // collapse older activity rows, keeping all comments
    let skip = hiddenCount;
    visible = items.filter((i) => (i.kind === 'activity' && skip > 0 ? (skip--, false) : true));
  }
  const watching = task.watcherIds.includes(me);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [comments.length]);

  return (
    <aside className="activity">
      <div className="activity-head">
        <span className="activity-title">Activity</span>
        <span className="spacer" />
        {searching ? (
          <div className="toolbar-search" style={{ width: 160 }}>
            <Search size={13} className="faint" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => !q && setSearching(false)} placeholder="Search comments" />
          </div>
        ) : (
          <button className="icon-btn sm" onClick={() => setSearching(true)}>
            <Search size={15} />
          </button>
        )}
        <Tooltip label={watching ? 'Stop watching' : 'Watch this task'}>
          <button className={clsx('activity-watch', { on: watching })} onClick={() => toggleWatcher(task.id)}>
            {watching ? <Bell size={14} /> : <BellOff size={14} />}
            <span>{task.watcherIds.length}</span>
          </button>
        </Tooltip>
        <Popover
          placement="bottom-end"
          content={(close) => (
            <Menu>
              <MenuItem label="All" checked={filter === 'all'} onClick={() => (setFilter('all'), close())} />
              <MenuItem label="Comments" checked={filter === 'comments'} onClick={() => (setFilter('comments'), close())} />
              <MenuItem label="Activity" checked={filter === 'activity'} onClick={() => (setFilter('activity'), close())} />
            </Menu>
          )}
        >
          <button className="icon-btn sm">
            <ListFilter size={15} />
          </button>
        </Popover>
      </div>
      <div className="activity-scroll" ref={scrollRef}>
        {hiddenCount > 0 && (
          <button className="activity-more" onClick={() => setShowAll(true)}>
            <ChevronDown size={13} /> Show {hiddenCount} more
          </button>
        )}
        {visible.map((i) =>
          i.kind === 'activity' ? (
            <div key={i.a.id} className="act-row">
              <span className="act-bullet" />
              <span className="act-text">
                <strong>{i.a.actorId === me ? 'You' : (db.users[i.a.actorId]?.name ?? 'Someone')}</strong> {describeActivity(db, i.a, task)}
              </span>
              <span className="act-time">{formatWhen(i.a.createdAt)}</span>
            </div>
          ) : (
            <CommentCard key={i.c.id} comment={i.c} replies={repliesOf(i.c.id)} taskId={task.id} />
          ),
        )}
        {visible.length === 0 && <div className="faint" style={{ padding: 20, fontSize: 'var(--fs-md)', textAlign: 'center' }}>Nothing here yet.</div>}
      </div>
      <Composer task={task} />
    </aside>
  );
}
